import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { useAuthStore } from '@auth/authStore';
import { getSites, getAssets } from '@entities';
import { useAsyncResource, useRegisterFilters, useUrlAssetScope, buildScopedSearch } from '@hooks';
import { FilterBar } from '@components';
import HomeMapCanvas from '../map/HomeMapCanvas';
import MapSelectionCard from '../map/MapSelectionCard';
import { normalizeMapRows, mapFilters, assetMapFilters, mapSearchText, mapDate } from '../map/map.model';
import styles from './MapExplorerView.module.css';

function MapWorkspace({ companyId, isActive }) {
  const { siteId, assetId, setScope } = useUrlAssetScope();
  const [params, setParams] = useSearchParams();
  const mode = ['sites', 'assets'].includes(params.get('mapMode')) ? params.get('mapMode') : assetId ? 'assets' : 'sites';
  const [fitRequest, setFitRequest] = useState(0);
  const sites = useAsyncResource(() => getSites(companyId), [companyId], { initialData: [] });
  const assets = useAsyncResource(() => getAssets(companyId), [companyId], { initialData: [] });
  const active = mode === 'assets' ? assets : sites;
  const listRef = useRef(null);

  const prepared = useMemo(() => {
    try {
      return { rows: normalizeMapRows(active.data, mode, Array.isArray(sites.data) ? sites.data : []), error: null };
    } catch (error) { return { rows: [], error: error.message }; }
  }, [active.data, mode, sites.data]);
  // Sites remain a fleet explorer. Assets are narrowed to the selected site,
  // while the selected asset stays a focus within that list.
  const rows = useMemo(() => prepared.rows.filter(row => mode !== 'assets' || !siteId || row.siteId === siteId),
    [prepared.rows, mode, siteId]);
  const filters = useRegisterFilters({
    namespace: 'map.' + mode, items: rows, definitions: mode === 'assets' ? assetMapFilters : mapFilters, getSearchText: mapSearchText,
  });
  const sorted = useMemo(() => [...filters.filtered].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' })), [filters.filtered]);
  const selected = prepared.rows.find(row => row.siteId === siteId && (mode === 'sites' || row.assetId === assetId)) || null;
  const markers = useMemo(() => sorted.filter(row => row.coordinates).map(row => ({
    id: row.id, label: row.label, mode: row.mode, ...row.coordinates,
  })), [sorted]);
  const hiddenSelection = selected && !sorted.some(row => row.id === selected.id);

  useEffect(() => {
    if (!isActive || !selected || hiddenSelection) return;
    const list = listRef.current;
    const item = list?.querySelector('[aria-pressed="true"]');
    if (item) list.scrollTop = Math.max(0, item.offsetTop - 12);
  }, [selected, hiddenSelection, isActive]);

  function select(row) {
    setScope({ siteId: row.siteId, assetId: mode === 'assets' ? row.assetId : null });
  }
  function browseAssets(row) {
    setParams(previous => {
      const next = new URLSearchParams(buildScopedSearch(previous.toString(),
        { siteId: row.siteId, assetId: null }, { mapMode: 'assets' }));
      next.delete('map.assets.q');
      assetMapFilters.forEach(filter => next.delete('map.assets.' + filter.key));
      return next;
    });
  }
  function changeMode(nextMode) {
    setParams(previous => { const next = new URLSearchParams(previous); next.set('mapMode', nextMode); return next; });
  }

  if (active.isLoading) return <div className={styles.feedback} role="status">Loading fleet locations…</div>;
  if (active.error || prepared.error) return <div className={styles.feedback}>
    <p role="alert">{active.error || prepared.error}</p>
    <button type="button" className={styles.button} onClick={() => active.reload()}>Retry locations</button>
  </div>;

  return <div className={styles.workspace}>
    <div className={styles.toolbar}>
      <div className={styles.mode} role="group" aria-label="Map locations">
        <button type="button" aria-pressed={mode === 'sites'} onClick={() => changeMode('sites')}>Sites</button>
        <button type="button" aria-pressed={mode === 'assets'} onClick={() => changeMode('assets')}>Assets</button>
      </div>
      <span>{mode === 'sites' ? 'Fleet sites' : siteId ? 'Assets at the selected site' : 'Fleet assets'}</span>
      <button type="button" className={styles.button} onClick={() => setScope({ siteId: null, assetId: null })}>Return to fleet</button>
    </div>
    <FilterBar filters={filters} noun={mode} />
    {mode === 'assets' && sites.error && <p className={styles.notice}>Site names could not be loaded; recorded IDs are shown.
      {' '}<button type="button" className={styles.link} onClick={() => sites.reload()}>Retry names</button></p>}
    {hiddenSelection && <p className={styles.notice}>The selected location is hidden by these filters. Its details remain below the map.
      {' '}<button type="button" className={styles.link} onClick={filters.clear}>Clear filters</button></p>}
    <div className={styles.layout}>
      <aside className={styles.results} aria-label="Location results">
        <header><strong>{sorted.length} {mode}</strong><span>{markers.length} mapped · {sorted.length - markers.length} without coordinates</span></header>
        <div ref={listRef} className={styles.list}>
          {sorted.map(row => <button key={row.id} type="button" className={styles.result}
            aria-pressed={selected?.id === row.id} onClick={() => select(row)}>
            <span className={styles.resultTop}><strong>{row.label}</strong><span className={styles.dot} data-tone={row.healthTone} aria-hidden="true" /></span>
            <span>{mode === 'assets' ? row.siteLabel + ' · ' : ''}{row.assetId || row.siteId}</span>
            <span className={styles.resultBottom}><span>{row.health}</span><span>{row.coordinates ? 'Mapped' : 'No coordinates'}</span></span>
            <small>Last inspection: {mapDate(row.lastInspection)}</small>
          </button>)}
          {!sorted.length && <div className={styles.empty}>No {mode} match this view.
            {rows.length > 0 && <button type="button" className={styles.button} onClick={filters.clear}>Clear filters</button>}</div>}
        </div>
      </aside>
      <section className={styles.mapPanel} aria-label="Map and selected location">
        <header className={styles.mapHeader}><div><strong>Fleet map</strong><span>Choose a pin or a location in the list</span></div>
          <button type="button" className={styles.button} disabled={!markers.length} onClick={() => setFitRequest(value => value + 1)}>Fit results</button>
        </header>
        <div className={styles.mapFrame}>
          <HomeMapCanvas markers={markers} selectedId={hiddenSelection ? null : selected?.id}
            onSelect={id => { const row = sorted.find(item => item.id === id); if (row) select(row); }}
            fitRequest={fitRequest} isActive={isActive} />
          {!markers.length && <p className={styles.mapEmpty}>No mapped locations in this view. Records without coordinates remain in the list.</p>}
        </div>
        <MapSelectionCard companyId={companyId} row={selected} onBrowseAssets={browseAssets} />
      </section>
    </div>
  </div>;
}

export default function MapExplorerView({ isActive = true }) {
  const companyId = useAuthStore(state => state.companyID);
  return companyId ? <MapWorkspace key={String(companyId)} companyId={companyId} isActive={isActive} /> :
    <p className={styles.feedback}>Select a company to explore its locations.</p>;
}